import React, { useState } from "react";
import {
  Configure,
  InstantSearch,
  useHits,
  useSearchBox,
} from "react-instantsearch-hooks-web";
import algoliasearch from "algoliasearch/lite";
import { Plus } from "lucide-react";
import DialogWrapper from "../../custom-ui/dialog/Dialog";
import CommunityAvatar from "../shared/CommunityAvatar";
import {
  canBeAssignedAsHubModerator,
  getInitials,
  normalizeEmail,
} from "../../../data/communityHub";
import toast from "react-hot-toast";

const searchClient = algoliasearch(
  process.env.REACT_APP_ALGOLIA_APP_ID!,
  process.env.REACT_APP_ALGOLIA_API_KEY!,
);

function ModeratorSearchInput() {
  const { refine } = useSearchBox();
  return (
    <input
      type="search"
      placeholder="Search admins or seekers by name"
      onChange={(e) => refine(e.target.value)}
      className="w-full px-4 py-2.5 border border-[#15415E]/40 rounded-lg text-sm text-[#15415E] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#15415E]/30"
    />
  );
}

function ModeratorHitList({
  excludeEmails,
  onAdd,
  addingId,
}: {
  excludeEmails: string[];
  onAdd: (hit: any) => void;
  addingId: string | null;
}) {
  const { hits } = useHits();
  const filtered = hits.filter((h: any) => {
    const role = h["claims.role"] || h.claims?.role;
    return (
      canBeAssignedAsHubModerator(role) &&
      !excludeEmails.includes(normalizeEmail(h.email))
    );
  });

  return (
    <div className="max-h-[240px] overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
      {filtered.length === 0 && (
        <p className="text-sm text-gray-500 py-8 text-center">
          No admins or seekers found
        </p>
      )}
      {filtered.map((hit: any) => {
        const id = hit.objectID;
        const name = hit.name || hit.email || "—";
        const email = hit.email;
        const showEmail =
          !!email && normalizeEmail(email) !== normalizeEmail(name);
        const isAdding = addingId === id;

        return (
          <div
            key={id}
            className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50"
          >
            <CommunityAvatar
              initials={getInitials(name)}
              name={name}
              avatarUrl={hit.photoURL}
              size={40}
            />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-[#15415E] truncate">
                {name}
              </p>
              {showEmail && (
                <p className="text-xs text-gray-500 truncate mt-0.5">{email}</p>
              )}
            </div>
            <button
              type="button"
              disabled={isAdding}
              onClick={() => onAdd(hit)}
              aria-label={isAdding ? "Adding moderator" : "Add moderator"}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-semibold text-[#15415E] bg-white border border-gray-200 rounded-lg shrink-0 transition-colors duration-150 hover:bg-[#E4EBEF] hover:border-[#1E596D]/40 disabled:opacity-50"
            >
              <Plus size={14} strokeWidth={2.5} />
              {isAdding ? "Adding…" : "Add"}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function SectionLabel({ children }: { children: any }) {
  return (
    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
      {children}
    </p>
  );
}

function CurrentModeratorCard({
  name,
  email,
}: {
  name: string;
  email?: string;
}) {
  const displayName = name || email || "—";
  const showEmail =
    !!email && normalizeEmail(email) !== normalizeEmail(displayName);

  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-white border border-gray-200 rounded-lg">
      <CommunityAvatar
        initials={getInitials(displayName)}
        name={displayName}
        size={40}
      />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm text-[#15415E] truncate">
          {displayName}
        </p>
        {showEmail && (
          <p className="text-xs text-gray-500 truncate mt-0.5">{email}</p>
        )}
      </div>
    </div>
  );
}

export default function ManageModeratorsModal({
  primaryModeratorName,
  primaryModeratorEmail,
  extraModerators,
  excludeEmails,
  onClose,
  onAddModerator,
  onSuccess,
}: {
  primaryModeratorName?: string;
  primaryModeratorEmail?: string;
  extraModerators: any[];
  excludeEmails: string[];
  onClose: () => void;
  onAddModerator: (picked: {
    id: string;
    name: string;
    email: string;
    role: string;
  }) => Promise<void>;
  onSuccess: () => void;
}) {
  const [addingId, setAddingId] = useState(null as string | null);
  const [localExclude, setLocalExclude] = useState(excludeEmails);

  const handleAdd = async (hit: any) => {
    const id = hit.objectID;
    setAddingId(id);
    try {
      const role = hit["claims.role"] || hit.claims?.role;
      const email = hit.email;
      const name = hit.name || email;

      if (!canBeAssignedAsHubModerator(role)) {
        toast.error("Only admins or seekers can be assigned as moderators");
        return;
      }

      await onAddModerator({
        id,
        name,
        email,
        role,
      });

      setLocalExclude((prev: string[]) => [
        ...prev,
        normalizeEmail(email),
      ]);
      toast.success("1 moderator successfully added");
      onSuccess();
    } catch {
      // onAddModerator shows error toasts for validation failures
    } finally {
      setAddingId(null);
    }
  };

  const allCurrent = [
    ...(primaryModeratorName || primaryModeratorEmail
      ? [
          {
            id: "primary",
            name: primaryModeratorName || primaryModeratorEmail || "—",
            email: primaryModeratorEmail,
          },
        ]
      : []),
    ...extraModerators.map((m: any) => ({
      id: m.id,
      name: m.name || m.email || "—",
      email: m.email,
    })),
  ];

  return (
    <DialogWrapper
      title="Manage moderators"
      size="max-w-lg"
      onClose={onClose}
      displayCloseIcon
    >
      <div className="-mt-2 space-y-6">
        <div>
          <SectionLabel>Current moderators</SectionLabel>
          <div className="space-y-2">
            {allCurrent.length === 0 && (
              <p className="text-sm text-gray-400 py-2">No moderators assigned</p>
            )}
            {allCurrent.map((mod) => (
              <CurrentModeratorCard
                key={mod.id}
                name={mod.name}
                email={mod.email}
              />
            ))}
          </div>
        </div>

        <div>
          <SectionLabel>Add moderator</SectionLabel>
          <InstantSearch searchClient={searchClient} indexName="UserQueries">
            <Configure
              filters="(claims.role:sadmin OR claims.role:admin OR claims.role:tac OR claims.role:SKR OR claims.role:COMM)"
              hitsPerPage={30}
            />
            <div className="space-y-3">
              <ModeratorSearchInput />
              <ModeratorHitList
                excludeEmails={localExclude}
                onAdd={handleAdd}
                addingId={addingId}
              />
            </div>
          </InstantSearch>
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 text-sm font-medium text-[#15415E] bg-white border border-gray-300 rounded-lg hover:bg-[#E4EBEF] hover:border-[#1E596D]/40 transition-colors duration-150"
          >
            Done
          </button>
        </div>
      </div>
    </DialogWrapper>
  );
}
